/**
 * Settings RBAC hooks (Issue #243).
 *
 * Wraps the typed `@jinzhuan/api-client` calls for the #242 RBAC contracts so
 * the `/settings` page can list preset roles, render the permission matrix,
 * read the caller's reveal/export quota, and (when the caller is super-admin
 * capable) load + mutate user role bindings.
 *
 * 红线：
 * - 所有数据通过 `@jinzhuan/api-client` typed client 调用，禁止手写 fetch /
 *   axios / 纯前端假状态。
 * - 角色绑定写入必须显式携带 `reason`，高危预置角色额外携带 `approvalNo` +
 *   `secondFactorResult='passed'`。校验由后端权威执行；前端只负责把表单值
 *   原样传给后端，并把 4xx/5xx 错误展示给操作人，绝不在前端伪装成功或自动
 *   补默认值。
 * - 写入成功后必须重新拉取后端列表，不依赖前端乐观状态。
 * - 错误消息来自后端 `ApiError` 结构化字段；不直接展示 5xx HTML/响应体。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createApiClient, type ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

export type PresetRoleDto = ApiComponents['schemas']['PresetRoleDto'];
export type PresetRoleListResponseDto = ApiComponents['schemas']['PresetRoleListResponseDto'];
export type PermissionMatrixResponseDto = ApiComponents['schemas']['PermissionMatrixResponseDto'];
export type RevealExportQuotaResponseDto = ApiComponents['schemas']['RevealExportQuotaResponseDto'];
export type RoleBindingDto = ApiComponents['schemas']['RoleBindingDto'];
export type CreateRoleBindingRequestDto = ApiComponents['schemas']['CreateRoleBindingRequestDto'];
export type UpdateRoleBindingRequestDto = ApiComponents['schemas']['UpdateRoleBindingRequestDto'];
export type RevokeRoleBindingRequestDto = ApiComponents['schemas']['RevokeRoleBindingRequestDto'];

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

export interface SettingsRbacCatalog {
  readonly presetRoles: AsyncState<PresetRoleListResponseDto>;
  readonly matrix: AsyncState<PermissionMatrixResponseDto>;
  readonly quota: AsyncState<RevealExportQuotaResponseDto>;
  refresh: () => Promise<void>;
}

/**
 * Loads the three read-only RBAC catalog endpoints in parallel:
 *   - GET /api/v1/auth/roles
 *   - GET /api/v1/auth/permissions/matrix
 *   - GET /api/v1/auth/reveal-export-quota
 *
 * Each slice keeps its own loading/error so a partial failure (e.g. quota 5xx)
 * does not blank the entire section.
 */
export function useSettingsRbacCatalog(): SettingsRbacCatalog {
  const [presetRoles, setPresetRoles] = useState<AsyncState<PresetRoleListResponseDto>>({
    data: null,
    loading: false,
    error: null,
  });
  const [matrix, setMatrix] = useState<AsyncState<PermissionMatrixResponseDto>>({
    data: null,
    loading: false,
    error: null,
  });
  const [quota, setQuota] = useState<AsyncState<RevealExportQuotaResponseDto>>({
    data: null,
    loading: false,
    error: null,
  });

  const loadPresetRoles = useCallback(async () => {
    setPresetRoles((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET('/api/v1/auth/roles');
      if (error) {
        setPresetRoles({ data: null, loading: false, error: pickErrorMessage(error, '加载预置角色失败') });
        return;
      }
      setPresetRoles({ data: data ?? null, loading: false, error: null });
    } catch (err) {
      setPresetRoles({ data: null, loading: false, error: pickErrorMessage(err, '加载预置角色失败') });
    }
  }, []);

  const loadMatrix = useCallback(async () => {
    setMatrix((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET('/api/v1/auth/permissions/matrix');
      if (error) {
        setMatrix({ data: null, loading: false, error: pickErrorMessage(error, '加载权限矩阵失败') });
        return;
      }
      setMatrix({ data: data ?? null, loading: false, error: null });
    } catch (err) {
      setMatrix({ data: null, loading: false, error: pickErrorMessage(err, '加载权限矩阵失败') });
    }
  }, []);

  const loadQuota = useCallback(async () => {
    setQuota((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET('/api/v1/auth/reveal-export-quota');
      if (error) {
        setQuota({ data: null, loading: false, error: pickErrorMessage(error, '加载 reveal/export 额度失败') });
        return;
      }
      setQuota({ data: data ?? null, loading: false, error: null });
    } catch (err) {
      setQuota({ data: null, loading: false, error: pickErrorMessage(err, '加载 reveal/export 额度失败') });
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadPresetRoles(), loadMatrix(), loadQuota()]);
  }, [loadPresetRoles, loadMatrix, loadQuota]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { presetRoles, matrix, quota, refresh };
}

export interface UseUserRoleBindingsReturn extends AsyncState<readonly RoleBindingDto[]> {
  readonly targetUserId: string | null;
  refresh: () => Promise<void>;
  createBinding: (input: CreateRoleBindingRequestDto) => Promise<RoleBindingDto | null>;
  updateBinding: (bindingId: string, input: UpdateRoleBindingRequestDto) => Promise<RoleBindingDto | null>;
  revokeBinding: (bindingId: string, input: RevokeRoleBindingRequestDto) => Promise<RoleBindingDto | null>;
}

/**
 * Loads + mutates role bindings for a single target user. Pass `null` to keep
 * the hook idle (no fetch) — useful when the operator has not yet entered a
 * user id.
 *
 * Mutations always re-fetch the bindings list on success; we do not maintain
 * frontend-only optimistic state for role-binding rows.
 */
export function useUserRoleBindings(targetUserId: string | null): UseUserRoleBindingsReturn {
  const [state, setState] = useState<AsyncState<readonly RoleBindingDto[]>>({
    data: null,
    loading: false,
    error: null,
  });
  const targetRef = useRef<string | null>(targetUserId);
  targetRef.current = targetUserId;

  const refresh = useCallback(async () => {
    const userId = targetRef.current;
    if (!userId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET('/api/v1/auth/users/{userId}/role-bindings', {
        params: { path: { userId } },
      });
      if (error) {
        setState({ data: null, loading: false, error: pickErrorMessage(error, '加载用户角色绑定失败') });
        return;
      }
      setState({
        data: (data?.bindings ?? []) as readonly RoleBindingDto[],
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载用户角色绑定失败') });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, targetUserId]);

  const createBinding = useCallback(
    async (input: CreateRoleBindingRequestDto): Promise<RoleBindingDto | null> => {
      const userId = targetRef.current;
      if (!userId) {
        throw new Error('未选择目标用户，无法创建角色绑定。');
      }
      const { data, error } = await apiClient.POST('/api/v1/auth/users/{userId}/role-bindings', {
        params: { path: { userId } },
        body: input,
      });
      if (error) {
        throw new Error(pickErrorMessage(error, '创建角色绑定失败'));
      }
      await refresh();
      return (data ?? null) as RoleBindingDto | null;
    },
    [refresh],
  );

  const updateBinding = useCallback(
    async (bindingId: string, input: UpdateRoleBindingRequestDto): Promise<RoleBindingDto | null> => {
      const userId = targetRef.current;
      if (!userId) {
        throw new Error('未选择目标用户，无法更新角色绑定。');
      }
      const { data, error } = await apiClient.PATCH(
        '/api/v1/auth/users/{userId}/role-bindings/{bindingId}',
        {
          params: { path: { userId, bindingId } },
          body: input,
        },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '更新角色绑定失败'));
      }
      await refresh();
      return (data ?? null) as RoleBindingDto | null;
    },
    [refresh],
  );

  const revokeBinding = useCallback(
    async (bindingId: string, input: RevokeRoleBindingRequestDto): Promise<RoleBindingDto | null> => {
      const userId = targetRef.current;
      if (!userId) {
        throw new Error('未选择目标用户，无法撤销角色绑定。');
      }
      const { data, error } = await apiClient.DELETE(
        '/api/v1/auth/users/{userId}/role-bindings/{bindingId}',
        {
          params: { path: { userId, bindingId } },
          body: input,
        },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '撤销角色绑定失败'));
      }
      await refresh();
      return (data ?? null) as RoleBindingDto | null;
    },
    [refresh],
  );

  return {
    ...state,
    targetUserId,
    refresh,
    createBinding,
    updateBinding,
    revokeBinding,
  };
}
